'use client';

import Box from '@mui/material/Box';
import { useId, type ReactNode } from 'react';

import { ArrowMarker, SVG_INK, SVG_SUBTLE, SvgLabel } from '@/components/landing/landing-svg';
import { BRAND } from '@/theme';

// Small hand-built previews for the "What are you working on?" picker. Each takes its labels
// from one translation string ("a|b|c"), in the order documented on the drawing function.

export type DiagramKind =
  | 'flowchart'
  | 'mindmap'
  | 'sequence'
  | 'timeline'
  | 'kanban'
  | 'gantt'
  | 'er'
  | 'pie'
  | 'org';

const INK = SVG_INK;
const SUBTLE = SVG_SUBTLE;
const SOFT = BRAND.pinkLight;
const STRONG = BRAND.pinkStrong;
const YELLOW = '#fff4c7';
const LINE = '#e7e1e4';
const box = { fill: '#ffffff', stroke: INK, strokeWidth: 1.5 } as const;
const edge = { fill: 'none', stroke: INK, strokeWidth: 1.4 } as const;

type DrawProps = { labels: string[]; arrow: string };

// New ticket | Urgent? | Fix now | Add to queue | Resolved | Yes | No
function Flowchart({ labels, arrow }: DrawProps) {
  const [start, question, fix, queue, done, yes, no] = labels;
  return (
    <>
      <g {...edge} markerEnd={`url(#${arrow})`}>
        <path d="M200 50 V62" />
        <path d="M136 100 H85 V148" />
        <path d="M264 100 H315 V148" />
        <path d="M85 190 V231 H138" />
        <path d="M315 190 V231 H262" />
      </g>
      <text x="108" y="92" fontSize="11" fill={SUBTLE} textAnchor="middle">{yes}</text>
      <text x="292" y="92" fontSize="11" fill={SUBTLE} textAnchor="middle">{no}</text>
      <rect x="140" y="10" width="120" height="40" rx="20" {...box} fill={SOFT} stroke={STRONG} />
      <SvgLabel x={140} y={10} width={120} height={40}>{start}</SvgLabel>
      <path d="M200 64 L264 100 L200 136 L136 100 Z" {...box} />
      <SvgLabel x={150} y={80} width={100} height={40} size={11.5}>{question}</SvgLabel>
      <rect x="30" y="150" width="110" height="40" rx="8" {...box} />
      <SvgLabel x={30} y={150} width={110} height={40}>{fix}</SvgLabel>
      <rect x="260" y="150" width="110" height="40" rx="8" {...box} />
      <SvgLabel x={260} y={150} width={110} height={40}>{queue}</SvgLabel>
      <rect x="140" y="212" width="120" height="38" rx="8" {...box} fill={BRAND.pink} stroke={STRONG} />
      <SvgLabel x={140} y={212} width={120} height={38}>{done}</SvgLabel>
    </>
  );
}

// Centre | six branches
function Mindmap({ labels }: DrawProps) {
  const [centre, ...branches] = labels;
  const spots: Array<[number, number]> = [
    [72, 46],
    [58, 130],
    [72, 214],
    [328, 46],
    [342, 130],
    [328, 214]
  ];
  const fills = [SOFT, '#ffffff', YELLOW];
  return (
    <>
      <g fill="none" stroke={STRONG} strokeWidth="1.6" strokeLinecap="round">
        {spots.map(([x, y]) => {
          const startX = x < 200 ? 150 : 250;
          const endX = x < 200 ? x + 50 : x - 50;
          return <path key={`${x}-${y}`} d={`M${startX} 130 C ${(startX + endX) / 2} 130, ${(startX + endX) / 2} ${y}, ${endX} ${y}`} />;
        })}
      </g>
      {spots.map(([x, y], index) => (
        <g key={`${x}-${y}`}>
          <rect x={x - 50} y={y - 17} width="100" height="34" rx="17" {...box} fill={fills[index % 3]} />
          <SvgLabel x={x - 50} y={y - 17} width={100} height={34} size={11.5}>{branches[index]}</SvgLabel>
        </g>
      ))}
      <ellipse cx="200" cy="130" rx="54" ry="30" {...box} fill={BRAND.pink} stroke={STRONG} strokeWidth="1.8" />
      <SvgLabel x={146} y={100} width={108} height={60} size={14} weight={700}>{centre}</SvgLabel>
    </>
  );
}

// Three participants | four messages (request, request, reply, reply)
function Sequence({ labels, arrow }: DrawProps) {
  const [a, b, c, ...messages] = labels;
  const xs = [70, 200, 330];
  const rows: Array<[number, number, number, boolean]> = [
    [0, 1, 84, false],
    [1, 2, 128, false],
    [2, 1, 172, true],
    [1, 0, 216, true]
  ];
  return (
    <>
      {xs.map((x) => (
        <path key={x} d={`M${x} 46 V250`} stroke="#c9c1c5" strokeWidth="1.2" strokeDasharray="4 4" />
      ))}
      {[a, b, c].map((name, index) => (
        <g key={index}>
          <rect x={xs[index]! - 52} y="12" width="104" height="34" rx="8" {...box} fill={index === 1 ? SOFT : '#ffffff'} stroke={index === 1 ? STRONG : INK} />
          <SvgLabel x={xs[index]! - 52} y={12} width={104} height={34}>{name}</SvgLabel>
        </g>
      ))}
      {rows.map(([from, to, y, reply], index) => {
        const fromX = xs[from]!;
        const toX = xs[to]!;
        const dir = toX > fromX ? 1 : -1;
        return (
          <g key={index}>
            <path
              d={`M${fromX + dir * 4} ${y} H${toX - dir * 6}`}
              {...edge}
              strokeDasharray={reply ? '5 4' : undefined}
              markerEnd={`url(#${arrow})`}
            />
            <SvgLabel x={Math.min(fromX, toX)} y={y - 22} width={130} height={20} size={11} weight={500} color={SUBTLE}>
              {messages[index]}
            </SvgLabel>
          </g>
        );
      })}
    </>
  );
}

// Four period names | four events
function Timeline({ labels, arrow }: DrawProps) {
  const periods = labels.slice(0, 4);
  const events = labels.slice(4, 8);
  const xs = [62, 154, 246, 338];
  return (
    <>
      <path d="M20 130 H384" {...edge} strokeWidth="2" markerEnd={`url(#${arrow})`} />
      {xs.map((x, index) => {
        const above = index % 2 === 0;
        const cardY = above ? 40 : 182;
        return (
          <g key={x}>
            <path d={above ? `M${x} 80 V114` : `M${x} 146 V182`} stroke={STRONG} strokeWidth="1.4" />
            <rect x={x - 44} y={cardY} width="88" height="40" rx="8" {...box} fill={index === 3 ? BRAND.pink : '#ffffff'} />
            <SvgLabel x={x - 44} y={cardY} width={88} height={40} size={11.5}>{events[index]}</SvgLabel>
            <circle cx={x} cy="130" r="15" fill="#ffffff" stroke={STRONG} strokeWidth="1.8" />
            <text x={x} y="134" fontSize="10.5" fontWeight="700" fill={INK} textAnchor="middle">{periods[index]}</text>
          </g>
        );
      })}
    </>
  );
}

// To do | Doing | Done | recap (done) | roadmap (doing) | three to-dos
function Kanban({ labels }: DrawProps) {
  const [todo, doing, done, recap, roadmap, ...todos] = labels;
  const columns: Array<{ title?: string; cards: Array<string | undefined>; accent: string }> = [
    { title: todo, cards: todos, accent: '#c9c1c5' },
    { title: doing, cards: [roadmap], accent: STRONG },
    { title: done, cards: [recap], accent: '#4db6ac' }
  ];
  return (
    <>
      {columns.map(({ title, cards, accent }, column) => {
        const x = 14 + column * 128;
        return (
          <g key={column}>
            <rect x={x} y="10" width="116" height="240" rx="12" fill="#f7f4f6" stroke={LINE} />
            <circle cx={x + 16} cy="32" r="4" fill={accent} />
            <SvgLabel x={x + 20} y={20} width={92} height={24} size={12} weight={700} align="left">{title}</SvgLabel>
            {cards.map((card, index) => (
              <g key={index}>
                <rect x={x + 8} y={52 + index * 50} width="100" height="42" rx="8" fill="#ffffff" stroke={LINE} />
                <rect x={x + 8} y={52 + index * 50} width="4" height="42" rx="2" fill={accent} />
                <SvgLabel x={x + 12} y={52 + index * 50} width={96} height={42} size={11} weight={500} align="left">{card}</SvgLabel>
              </g>
            ))}
          </g>
        );
      })}
    </>
  );
}

// Four task names | six period headings
function Gantt({ labels }: DrawProps) {
  const tasks = labels.slice(0, 4);
  const weeks = labels.slice(4, 10);
  const left = 104;
  const col = 47;
  const bars: Array<[number, number, string]> = [
    [0, 2, BRAND.pink],
    [1.5, 3, SOFT],
    [4, 1.6, YELLOW]
  ];
  return (
    <>
      {weeks.map((week, index) => (
        <g key={index}>
          <path d={`M${left + index * col} 22 V246`} stroke={LINE} strokeDasharray={index === 0 ? undefined : '3 4'} />
          <text x={left + index * col + col / 2} y="38" fontSize="10.5" fontWeight="600" fill={SUBTLE} textAnchor="middle">{week}</text>
        </g>
      ))}
      {tasks.map((task, index) => (
        <SvgLabel key={index} x={4} y={60 + index * 46} width={left - 8} height={32} size={11.5} align="left">{task}</SvgLabel>
      ))}
      {bars.map(([start, span, fill], index) => (
        <rect
          key={index}
          x={left + start * col + 3}
          y={64 + index * 46}
          width={span * col - 6}
          height="24"
          rx="7"
          fill={fill}
          stroke={index === 0 ? STRONG : INK}
          strokeWidth="1.3"
        />
      ))}
      <path d={`M${left + 5.7 * col} 204 l10 10 l-10 10 l-10 -10 z`} fill={STRONG} />
    </>
  );
}

// Three entities | two relationship verbs
function EntityRelation({ labels }: DrawProps) {
  const [customer, order, product, places, contains] = labels;
  const fields = [
    ['id', 'name', 'email'],
    ['id', 'date', 'total'],
    ['id', 'title', 'price']
  ];
  const entities: Array<[string | undefined, number, number]> = [
    [customer, 16, 16],
    [order, 152, 140],
    [product, 288, 16]
  ];
  return (
    <>
      <g {...edge}>
        <path d="M66 124 V190 H152" />
        <path d="M338 124 V190 H248" />
      </g>
      {/* Crow's feet on the "many" ends. */}
      <g {...edge}>
        <path d="M140 190 L152 182 M140 190 L152 198" />
        <path d="M260 190 L248 182 M260 190 L248 198" />
        <path d="M330 124 L338 136 L346 124" />
      </g>
      <SvgLabel x={70} y={160} width={80} height={20} size={11} weight={500} color={SUBTLE} align="left">{places}</SvgLabel>
      <SvgLabel x={254} y={160} width={80} height={20} size={11} weight={500} color={SUBTLE} align="left">{contains}</SvgLabel>
      {entities.map(([name, x, y], index) => (
        <g key={index}>
          <rect x={x} y={y} width="96" height="108" rx="8" fill="#ffffff" stroke={INK} strokeWidth="1.5" />
          <path d={`M${x} ${y + 8} a8 8 0 0 1 8 -8 h80 a8 8 0 0 1 8 8 v22 h-96 z`} fill={index === 1 ? BRAND.pink : SOFT} stroke={INK} strokeWidth="1.5" />
          <SvgLabel x={x} y={y} width={96} height={30} size={12} weight={700}>{name}</SvgLabel>
          {fields[index]!.map((field, row) => (
            <text key={field} x={x + 12} y={y + 50 + row * 24} fontSize="11" fill={SUBTLE} fontFamily="ui-monospace, Menlo, monospace">
              {field}
            </text>
          ))}
        </g>
      ))}
    </>
  );
}

// Four slice names
function Pie({ labels }: DrawProps) {
  const shares = [0.44, 0.26, 0.18, 0.12];
  const colors = ['#f06292', '#64b5f6', '#ffb74d', '#4db6ac'];
  const cx = 120;
  const cy = 130;
  const r = 96;
  let angle = -Math.PI / 2;
  const slices = shares.map((share, index) => {
    const start = angle;
    angle += share * Math.PI * 2;
    const large = share > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(angle);
    const y2 = cy + r * Math.sin(angle);
    return (
      <path
        key={index}
        d={`M${cx} ${cy} L${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} Z`}
        fill={colors[index]}
        stroke="#ffffff"
        strokeWidth="2.5"
      />
    );
  });
  return (
    <>
      {slices}
      {labels.slice(0, 4).map((label, index) => (
        <g key={index} transform={`translate(240 ${70 + index * 34})`}>
          <rect width="14" height="14" rx="4" fill={colors[index]} />
          <SvgLabel x={18} y={-6} width={100} height={26} size={12} align="left">{label}</SvgLabel>
          <text x="148" y="11.5" fontSize="12" fontWeight="700" fill={INK} textAnchor="end">
            {Math.round(shares[index]! * 100)}%
          </text>
        </g>
      ))}
    </>
  );
}

// Head | three departments | one team under the first, two under the second
function OrgChart({ labels }: DrawProps) {
  const [head, first, second, third, ...teams] = labels;
  const node = (label: string | undefined, x: number, y: number, width: number, fill = '#ffffff') => (
    <g>
      <rect x={x - width / 2} y={y} width={width} height="38" rx="8" {...box} fill={fill} stroke={fill === BRAND.pink ? STRONG : INK} />
      <SvgLabel x={x - width / 2} y={y} width={width} height={38} size={11.5}>{label}</SvgLabel>
    </g>
  );
  return (
    <>
      <g {...edge}>
        <path d="M200 50 V76 M70 76 H330 M70 76 V100 M200 76 V100 M330 76 V100" />
        <path d="M70 138 V196 M200 138 V166 M170 166 H280 M170 166 V196 M280 166 V196" />
      </g>
      {node(head, 200, 12, 104, BRAND.pink)}
      {node(first, 70, 100, 112, SOFT)}
      {node(second, 200, 100, 112, SOFT)}
      {node(third, 330, 100, 112, SOFT)}
      {node(teams[0], 70, 196, 88)}
      {node(teams[1], 170, 196, 88)}
      {node(teams[2], 280, 196, 88)}
    </>
  );
}

const DRAWINGS: Record<DiagramKind, (props: DrawProps) => ReactNode> = {
  flowchart: Flowchart,
  mindmap: Mindmap,
  sequence: Sequence,
  timeline: Timeline,
  kanban: Kanban,
  gantt: Gantt,
  er: EntityRelation,
  pie: Pie,
  org: OrgChart
};

export function DiagramPreview({ kind, labels }: { kind: DiagramKind; labels: string[] }) {
  const arrow = useId();
  const Draw = DRAWINGS[kind];
  return (
    <Box
      component="svg"
      viewBox="0 0 400 260"
      aria-hidden="true"
      sx={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}
    >
      <defs>
        <ArrowMarker id={arrow} />
      </defs>
      <Draw labels={labels} arrow={arrow} />
    </Box>
  );
}
